import { ALL_CATEGORIES_ID_NUM } from '@/constants/category'
import { ALL_CANADA_LOCATION_ID } from '@/constants/location'

import { isDominantCategorySearch } from './isDominantCategorySearch'

export type DecodedUrl = {
  /**
   * Category ID should be undefined when is a dom-cat
   */
  categoryId: number
  location: DecodedLocation
  keywords?: string
  isDomCat: boolean
}

export type DecodedLocation =
  | { id: number; isArea: false }
  | {
      id: number
      area: {
        latitude: number
        longitude: number
        radius: number
      }
      isArea: true
    }

/*
 * Extracts the category ID, location ID, and keywords from a Kijiji URL.
 * Note that this is a simple decoder and for more advanced use cases, you
 * should use the seo-service API.
 */
export function decodeUrl(url: URL): DecodedUrl {
  const urlString = url.toString()

  const categoryRegex = /c(\d+)/
  const locationRegex = /l(\d+)/

  const categoryMatch = urlString.match(categoryRegex)
  const locationMatch = urlString.match(locationRegex)

  // Get Latitude and Longitude from URL. Eg: ?ll=43.684932%2C-79.336705
  const latLong = url.searchParams.get('ll')
  const latitude = latLong ? parseFloat(latLong.split(',')[0]) : null
  const longitude = latLong ? parseFloat(latLong.split(',')[1]) : null

  // Get Radius from URL. Eg: ?radius=100
  const radius = url.searchParams.get('radius')
    ? parseInt(url.searchParams.get('radius') as string, 10)
    : null

  const hasLocationArea = latitude && longitude && radius

  const isDomCat =
    !categoryMatch && categoryMatch !== ALL_CATEGORIES_ID_NUM && isDominantCategorySearch(url.href)

  // If the category ID is not present in the URL, then it is the "All Categories" category.
  const categoryId = categoryMatch ? parseInt(categoryMatch[1], 10) : ALL_CATEGORIES_ID_NUM
  const locationId = locationMatch ? parseInt(locationMatch[1], 10) : ALL_CANADA_LOCATION_ID

  const extractedData: DecodedUrl = {
    isDomCat,
    categoryId: categoryId,
    location: hasLocationArea
      ? {
          id: locationId,
          area: {
            latitude: latitude,
            longitude: longitude,
            radius: radius,
          },
          isArea: true,
        }
      : { id: locationId, isArea: false },
  }

  const keywords = getKeywords(urlString)

  if (keywords) {
    extractedData.keywords = keywords.replace(/-/g, ' ').replace(/%2F/g, '/')
  } else {
    /** It can't be a domCat search if there is no keyword */
    extractedData.isDomCat = false
  }

  return extractedData
}

function getKeywords(url: string): string | null {
  const urlSegments = url.split('/')

  if (urlSegments.length === 0) {
    return null
  }

  const positionOfPageTypePathElement = urlSegments.findIndex((segment) => segment.startsWith('b-'))
  if (positionOfPageTypePathElement === -1) {
    return null
  }

  const lastPathElement = urlSegments[urlSegments.length - 1]
  if (!lastPathElement.startsWith('k0')) {
    return null
  }

  const positionOfKeywordsPathElement =
    positionOfPageTypePathElement + (lastPathElement.startsWith('k0c') ? 2 : 1)
  if (urlSegments.length <= positionOfKeywordsPathElement) {
    return null
  }

  return urlSegments[positionOfKeywordsPathElement]
}
